import { Subscription } from 'rxjs';
// Angular
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
// Layout
import { LayoutConfigService, SplashScreenService } from './core/_base/layout';
import { I18nService } from './core/i18n';
import { currentUser } from './core/auth/auth.selectors';
import { IdentityService } from './core/identity/identity.service';
import * as AuthActions from './core/auth/auth.actions';
import { distinctUntilChanged, filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { AppState } from './core/core.state';
import { SleepWatcherService } from './core/services/sleepwatcher.service';
import { Toast } from 'ngx-toastr';
import { showToastrInfo } from './core/store/toastr.actions';
import { HubDisconnected, HubReconnectRequest } from './core/store/layout.actions';
import { ConversationsService } from './core/services/conversations.service';

@Component({
    selector: 'body[app-root]',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
	// Public properties
	title = 'Charla';
	loader: boolean;
	private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/

	/**
	 * Component constructor
	 *
	 * @param i18n: I18nService
	 * @param router: Router
	 * @param layoutConfigService: LayoutCongifService
	 * @param splashScreenService: SplashScreenService
	 */
	constructor(private i18n: I18nService,
				private router: Router,
				private layoutConfigService: LayoutConfigService,
				private splashScreenService: SplashScreenService,
				private authService: IdentityService,
				private store: Store<AppState>,
				private sleepWatcher: SleepWatcherService,
				private hub: ConversationsService) {

		// the user's saved interface language wins over the browser's
		this.unsubscribe.push(
			this.store.select(currentUser).pipe(
				filter(u => !!u?.interface_language),
				map(u => u.interface_language),
				distinctUntilChanged()
			).subscribe(lang => this.i18n.use(lang))
		);
	
		this.sleepWatcher.sleepEvents$.pipe(
			withLatestFrom( this.store.select(state => state.layout.isHubDisconnected) ),
			switchMap(([event, isHubDisconnected]) =>
				this.hub.ping().pipe(
					// emit all three values for the subscriber
					map(pingResponse => [event, isHubDisconnected, pingResponse] as [string, boolean, string])
				)
			)		
		).subscribe(([event, isHubDisconnected, pingResponse]) => {

			if (event === 'offline' && !isHubDisconnected) {
				// if connection lost, we set the hub to disconnected in case the onclose event does not fire
				this.store.dispatch(HubDisconnected({is_disconnected: true}));
			} else if (event === 'online' && (isHubDisconnected || pingResponse === 'failed')) {
				// if we are back online, and we are still disconnected, we try to reconnect
				this.store.dispatch(HubReconnectRequest());
			} else if (event === 'gap' && (isHubDisconnected || pingResponse === 'failed')) {
				// if we woke up from sleep and we are still disconnected, we try to reconnect
				this.store.dispatch(HubReconnectRequest());
			} else if (event === 'visible' && (isHubDisconnected || pingResponse === 'failed')) {
				// if we woke up from sleep and we are still disconnected, we try to reconnect
				this.store.dispatch(HubReconnectRequest());
			} 

			//console.info('Woke up because:', event);
			//this.store.dispatch(showToastrInfo({message: 'Watcher Status changed to ➡️ ' + event, title: 'Sleep Watcher'}));
		});

		// right solution was to put the runLoginSequence in the base.component
		// however it never get fires because of the AuthGuards in the child routes 
		// @see https://stackoverflow.com/questions/49428112/canactivate-guards-on-child-routes-run-before-parent-resolve-finish
		let auth_routes = [
            '/auth/login', '/auth/register', '/auth/forgot-password',
			'/auth/logout', '/error', '/should-login', '/externalshop', '/externalmessenger'
		];
        if ( !auth_routes.some(route => window.location.pathname.includes(route))) {
            this.authService.runInitialLoginSequence();
        } else {
			console.debug("Identity pages, skipping runInitialLoginSequence");
		}

		this.authService.canActivateProtectedRoutes$.pipe(
			filter(s => s),
			take(1)
		).subscribe(s => {
			console.debug("User is signed in, getting the user profile");
			this.store.dispatch(AuthActions.UserRequested());
		});

	}


	/**
	 * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
	 */

	/**
	 * On init
	 */
	ngOnInit(): void {
		// enable/disable loader
		this.loader = this.layoutConfigService.getConfig('loader.enabled');

		const routerSubscription = this.router.events.subscribe(event => {
			if (event instanceof NavigationEnd) {
				// hide splash screen
				//this.splashScreenService.hide();

				// scroll to top on every route change
				window.scrollTo(0, 0);

				// to display back the body content
				setTimeout(() => {
					document.body.classList.add('kt-page--loaded');
				}, 500);
			}
		});
		this.unsubscribe.push(routerSubscription);
	}

	/**
	 * On Destroy
	 */
	ngOnDestroy() {
		this.unsubscribe.forEach(sb => sb.unsubscribe());
	}
}
